import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hasNavPermission } from './ProtectedRoute';
import { ADMIN_PERMISSION_KEYS, PRODUCTION_PERMISSION_KEYS } from '../permissions';
import api from '../api/axiosConfig';
import './Navbar.css';
import { REMISIONES_CHANGED_EVENT } from '../utils/remisionBoxes';

const PAGE_NAMES = {
  '/': 'Inicio',
  '/administration': 'Administración',
  '/settings': 'Ajustes',
  '/companies': 'Empresas',
  '/farms': 'Fincas',
  '/bunch-sleevers': 'Enfundadores',
  '/ribbons': 'Cintas',
  '/production': 'Producción',
  '/enfunde': 'Control de Enfunde',
  '/enfunde/list': 'Historial de Enfunde',
  '/cosecha': 'Detalles de Cosecha',
  '/cosecha/historial': 'Historial de Cosecha',
  '/recobro': 'Recobro de Cintas',
  '/production-summary': 'Resumen de Producción',
  '/production-budget': 'Presupuesto de Cajas',
  '/fumigacion': 'Fumigación Aérea',
  '/fumigacion/historial': 'Historial de Fumigación',
  '/ventas': 'Ventas',
  '/despachos': 'Despachos',
  '/despachos/remision': 'Formulario de Remisión',
  '/despachos/historial': 'Historial de Remisiones',
  '/ventas/formulario': 'Formulario de Ventas',
  '/ventas/historial': 'Historial de Ventas',
  '/ventas/resumen': 'Resumen de Ventas',
  '/users': 'Usuarios',
  '/exporters': 'Exportadores',
  '/brands': 'Marcas',
  '/fumigation-products': 'Productos de Fumigación',
  '/fumigation-companies': 'Empresas de Fumigación',
  '/fumigation-pilots': 'Pilotos de Fumigación',
  '/transportations': 'Transportes',
  '/destinations': 'Destinos',
  '/drivers': 'Conductores',
  '/box-types': 'Tipos de Caja',
  '/inspectors': 'Inspectores',
  '/novedades': 'Novedades',
  '/profile': 'Perfil',
};

export const NOVEDADES_SEEN_KEY = 'farmershub_novedades_seen_hash';
// Fired (on window) after a sale's price changes, so the Ventas badge updates right away.
export const SALE_PRICES_CHANGED_EVENT = 'farmershub:sale-prices-changed';

export function getPageName(path) {
  if (!path) return 'Farmers Hub';
  if (path.startsWith('/ventas/formulario/')) return 'Editar Venta';
  if (path.startsWith('/despachos/remision/')) return 'Editar Remisión';
  if (/^\/cosecha\/\d+$/.test(path)) return 'Editar Detalles de Cosecha';
  if (/^\/fumigacion\/\d+$/.test(path)) return 'Editar Fumigación Aérea';
  return PAGE_NAMES[path] || path.replace(/^\//, '').replace(/-/g, ' ') || 'Home';
}

export function formatDuration(totalSeconds) {
  if (totalSeconds == null || !Number.isFinite(Number(totalSeconds))) return '—';
  const seconds = Math.max(0, Math.floor(Number(totalSeconds)));
  if (seconds < 60) return `${seconds} s`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours < 24) {
    return remainingMinutes ? `${hours} h ${remainingMinutes} min` : `${hours} h`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours ? `${days} d ${remainingHours} h` : `${days} d`;
}

export function formatActiveDuration(activeSince, now = Date.now()) {
  const startedAt = Date.parse(activeSince);
  if (!Number.isFinite(startedAt)) return null;
  return formatDuration((now - startedAt) / 1000);
}

export default function Navbar({ collapsed, onToggleCollapse }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [hasNewNovedades, setHasNewNovedades] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const heartbeatRef = useRef(null);
  const onlineRef = useRef(null);
  const novedadesRef = useRef(null);
  const canNovedades = hasNavPermission(user, 'novedades');
  // Sales missing a Precio 43 lbs: shown on the Ventas item to users who can set prices.
  const canFixPrices = hasNavPermission(user, 'ventas') && hasNavPermission(user, 'sale_prices');
  const [missingPrices, setMissingPrices] = useState(0);
  // Sales whose remisiones don't add up to the sale's Cajas: shown on the Despachos item.
  const watchesRemisiones = hasNavPermission(user, 'despachos');
  const [boxMismatches, setBoxMismatches] = useState(0);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  // Heartbeat — keep this session marked online and record the current page,
  // immediately on route changes and every 30 seconds.
  const currentPage = getPageName(location.pathname);
  useEffect(() => {
    if (!user) return;
    const beat = () => api.post('users/heartbeat/', { page: currentPage }).catch(() => {});
    beat();
    heartbeatRef.current = setInterval(beat, 30_000);
    return () => clearInterval(heartbeatRef.current);
  }, [user, currentPage]);

  // Poll who else is online, every 30s.
  useEffect(() => {
    if (!user) return;
    const fetchOnline = () =>
      api.get('users/online/')
        .then((r) => setOnlineUsers(r.data || []))
        // Fail closed: drop the stale list (with any details) if polling breaks.
        .catch(() => setOnlineUsers([]));
    fetchOnline();
    onlineRef.current = setInterval(fetchOnline, 30_000);
    return () => clearInterval(onlineRef.current);
  }, [user]);

  // Check for unseen novedades — re-checks on navigation too, so visiting
  // /novedades (which marks the latest hash as seen) clears the pulse right away.
  useEffect(() => {
    if (!user || !canNovedades) return;
    const checkNovedades = () => {
      api.get('novedades/').then((r) => {
        if (!r.data || r.data.length === 0) { setHasNewNovedades(false); return; }
        let seen = null;
        try { seen = localStorage.getItem(NOVEDADES_SEEN_KEY); } catch { /* ignore */ }
        setHasNewNovedades(r.data[0].hash !== seen);
      }).catch(() => {});
    };
    checkNovedades();
    novedadesRef.current = setInterval(checkNovedades, 60_000);
    return () => clearInterval(novedadesRef.current);
  }, [user, canNovedades, location.pathname]);

  useEffect(() => {
    if (!user || !watchesRemisiones) { setBoxMismatches(0); return undefined; }
    const checkBoxes = () => {
      api.get('dispatch-referrals/box-mismatches/').then((r) => setBoxMismatches(r.data.count)).catch(() => {});
    };
    checkBoxes();
    const timer = setInterval(checkBoxes, 60_000);
    window.addEventListener(REMISIONES_CHANGED_EVENT, checkBoxes);
    return () => {
      clearInterval(timer);
      window.removeEventListener(REMISIONES_CHANGED_EVENT, checkBoxes);
    };
  }, [user, watchesRemisiones, location.pathname]);

  useEffect(() => {
    if (!user || !canFixPrices) { setMissingPrices(0); return undefined; }
    const checkPrices = () => {
      api.get('sales/missing-prices/').then((r) => setMissingPrices(r.data.count)).catch(() => {});
    };
    checkPrices();
    const timer = setInterval(checkPrices, 60_000);
    window.addEventListener(SALE_PRICES_CHANGED_EVENT, checkPrices);
    return () => {
      clearInterval(timer);
      window.removeEventListener(SALE_PRICES_CHANGED_EVENT, checkPrices);
    };
  }, [user, canFixPrices, location.pathname]);

  if (!user) return null;

  const isAdmin = !!(user.is_staff || user.is_superuser);
  const canProduction = PRODUCTION_PERMISSION_KEYS.some((key) => hasNavPermission(user, key));
  const canVentas = hasNavPermission(user, 'ventas');
  const canDespachos = hasNavPermission(user, 'despachos');
  const canAdmin = isAdmin || ADMIN_PERMISSION_KEYS.some((key) => hasNavPermission(user, key));
  const isAt = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeIfMobile = () => setMobileOpen(false);

  const othersOnline = onlineUsers.filter((u) => u.id !== user.id);

  const content = (
    <>
      <div className="nb-header">
        <button className="nb-logo-btn" onClick={onToggleCollapse} aria-label="Toggle sidebar">
          <span className="nb-logo-icon">🌾</span>
          {!collapsed && <span className="nb-logo-text">Farmers Hub</span>}
        </button>
      </div>

      <nav className="nb-nav" aria-label="Main navigation">
        <Link to="/" className={`nb-item${isAt('/') ? ' active' : ''}`} onClick={closeIfMobile}>
          <span className="nb-icon">🏠</span>
          <span className="nb-label">Inicio</span>
        </Link>

        {canProduction && (
          <Link
            to="/production"
            className={`nb-item${isAt('/production') || isAt('/enfunde') || isAt('/cosecha') || isAt('/recobro') || isAt('/fumigacion') ? ' active' : ''}`}
            onClick={closeIfMobile}
          >
            <span className="nb-icon">🌾</span>
            <span className="nb-label">Producción</span>
          </Link>
        )}

        {canVentas && (
          <Link
            to="/ventas"
            className={`nb-item${isAt('/ventas') ? ' active' : ''}`}
            onClick={closeIfMobile}
            title={missingPrices > 0 ? `${missingPrices} ${missingPrices === 1 ? 'venta sin' : 'ventas sin'} Precio 43 lbs` : undefined}
          >
            <span className="nb-icon nb-icon-badge">
              💰
              {missingPrices > 0 && <span className="nb-count-badge">{missingPrices > 99 ? '99+' : missingPrices}</span>}
            </span>
            <span className="nb-label">Ventas</span>
          </Link>
        )}

        {canDespachos && (
          <Link
            to="/despachos"
            className={`nb-item${isAt('/despachos') ? ' active' : ''}`}
            onClick={closeIfMobile}
            title={boxMismatches > 0 ? `${boxMismatches} ${boxMismatches === 1 ? 'venta' : 'ventas'} con cajas de remisiones que no cuadran` : undefined}
          >
            <span className="nb-icon nb-icon-badge">
              🚛
              {boxMismatches > 0 && <span className="nb-count-badge">{boxMismatches > 99 ? '99+' : boxMismatches}</span>}
            </span>
            <span className="nb-label">Despachos</span>
          </Link>
        )}

        {canAdmin && (
          <Link to="/administration" className={`nb-item${isAt('/administration') || isAt('/settings') ? ' active' : ''}`} onClick={closeIfMobile}>
            <span className="nb-icon">⚙️</span>
            <span className="nb-label">Admin</span>
          </Link>
        )}

        {canNovedades && (
          <Link to="/novedades" className={`nb-item${isAt('/novedades') ? ' active' : ''}`} onClick={closeIfMobile}>
            <span className="nb-icon nb-icon-badge">
              📰
              {hasNewNovedades && <span className="nb-pulse-dot" />}
            </span>
            <span className="nb-label">Novedades</span>
          </Link>
        )}
      </nav>

      <div className="nb-footer">
        {othersOnline.length > 0 && (
          <div className="nb-online-list">
            <div className="nb-online-label">
              <span className="nb-online-dot" />
              <span>{othersOnline.length} en línea</span>
            </div>
            {othersOnline.map((u) => {
              // The server only includes these fields for viewers it has
              // authorized (superuser or explicit grant), so we follow the
              // payload rather than a possibly-stale cached permission.
              const hasActivityDetails = Object.prototype.hasOwnProperty.call(u, 'current_page');
              // Use the server-computed duration so a viewer's local clock
              // cannot skew the "how long active" number.
              const activeDuration = formatDuration(u.active_duration_seconds);
              const activeSince = u.active_since && Number.isFinite(Date.parse(u.active_since))
                ? new Date(u.active_since).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                : null;

              return (
                <div key={u.id} className="nb-online-user">
                  <span className="nb-online-avatar">
                    {(u.first_name || u.username || '?').charAt(0).toUpperCase()}
                  </span>
                  <span
                    className="nb-online-name-wrap"
                    tabIndex={hasActivityDetails ? 0 : undefined}
                  >
                    <span className="nb-online-name">
                      {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.username}
                    </span>
                    {hasActivityDetails && (
                      <span className="nb-online-tooltip" role="tooltip">
                        <span className="nb-online-tooltip-line">
                          <strong>Página:</strong> {u.current_page || '—'}
                        </span>
                        <span className="nb-online-tooltip-line">
                          <strong>Activo:</strong> {activeDuration || '—'}
                        </span>
                        {activeSince && (
                          <span className="nb-online-tooltip-since">Desde las {activeSince}</span>
                        )}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        <div className="nb-user-card">
          <div className="nb-avatar">{(user.first_name || user.username || '?').charAt(0).toUpperCase()}</div>
          <div className="nb-user-info">
            <div className="nb-user-name">{user.first_name || user.username}</div>
            <div className="nb-user-role">{isAdmin ? 'Admin' : 'Miembro'}</div>
          </div>
        </div>
        <Link to="/profile" className="nb-item" onClick={closeIfMobile}>
          <span className="nb-icon">👤</span>
          <span className="nb-label">Perfil</span>
        </Link>
        <button className="nb-item nb-logout-btn" onClick={handleLogout}>
          <span className="nb-icon">🚪</span>
          <span className="nb-label">Cerrar sesión</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      <aside className={`nb-root${collapsed ? ' nb-collapsed' : ''}`} aria-label="Sidebar">
        {content}
      </aside>

      <div className="nb-mobile-topbar">
        <button className="nb-hamburger" onClick={() => setMobileOpen(true)} aria-label="Open navigation">
          <span /><span /><span />
        </button>
        <Link to="/" className="nb-mobile-brand">🌾 Farmers Hub</Link>
      </div>

      <div className={`nb-overlay${mobileOpen ? ' open' : ''}`} onClick={() => setMobileOpen(false)} aria-hidden="true" />

      <aside className={`nb-root nb-mobile-sidebar${mobileOpen ? ' open' : ''}`} aria-label="Mobile navigation">
        {content}
      </aside>
    </>
  );
}
